import { createRef, useCallback, useEffect, useRef, useState } from 'react';
import { Image, OverlayTrigger, Tooltip } from 'react-bootstrap';

import IncomingMessage from './IncomingMessage';
import OutgoingMessage from './OutgoingMessage';
import { useAppDispatch } from '../../../store/hooks';
import { NUMBERS } from '../../../resources/number-constants';
import { getChats, jobAIChat, moveICPSuggestion } from '../../../store/features/dashboard';
import { backBtn, chatPlaneIcon, climateLogo } from '../../../assets/images/images';
import { UserCompanyDetails, ChatMessage, useFetchUserDetails } from '../../../utility/chatUtils';

const ChatWithICP = ({
    conversationId,
    generateJobDescription,
    jobId,
    step,
    hideChatWindow,
    setHideChatWindow,
    refreshJobData
}: any) => {
    const dispatch = useAppDispatch();
    const [chatList, setChatList] = useState<ChatMessage[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const messageRef = createRef<HTMLTextAreaElement>();
    const [inputMessage, setInputMessage] = useState('');
    const [lastMessageId, setLastMessageId] = useState<number | null>(null);
    const [showSendButton, setShowSendButton] = useState(false);
    const [userCompanyDetails, setUserCompanyDetails] = useState<UserCompanyDetails>({ logo: '', userId: '' });
    const [hasLoaded, setHasLoaded] = useState(false);


    const chatContainerRef = useRef<HTMLDivElement>(null); // Ref for chat container

    // Scroll to the bottom of chat
    const scrollToBottom = () => {
        if (chatContainerRef.current) {
            chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
        }
    };

    // Input change handler for trimming spaces and showing send button
    const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const trimmedInput = e.target.value.trim();

        setInputMessage(trimmedInput.length > 0 ? e.target.value : '');
        if (trimmedInput.length > 0) {
            setShowSendButton(true);
        } else {
            setShowSendButton(false);
        }
    };

    // Fetch chat details with loader handling
    const fetchChatDetails = useCallback(async () => {
        try {
            setIsLoading(true);
            const convId = conversationId;
            if (!convId) {
                setIsLoading(false);
                return;
            }
            const payload = { conversation_id: convId, limit: 300, step };
            const res = await dispatch(getChats(payload)).unwrap();

            // Check if we need to add initial message after loading chat history
            const hasGeneralInfoMessages = res?.data?.some((msg: any) =>
                msg.result?.general_info === true || msg.result?.out_of_context === true
            );



            setChatList(res?.data || []);

            // Add initial message if there are general info messages and no existing initial message
            if (hasGeneralInfoMessages) {
                const hasExistingInitialMessage = res?.data?.some((msg: any) =>
                    msg.result?.is_initial === true
                );

                if (!hasExistingInitialMessage) {
                    setTimeout(() => addInitialMessage(), 100);
                }
            }


        } catch (error) {
            console.info('Error during fetching chat details:', error);
        } finally {
            setIsLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, conversationId, step]);

    // Add initial message after any AI response
    const addInitialMessage = useCallback(() => {
        const convId = conversationId;
        if (!convId) {
            return;
        }

        const initialMsg: ChatMessage = {
            id: `temp_${Date.now()}`, // Use temporary ID that won't be used as parent_id
            payload_type: 4, // NUMBERS.FOUR - required for IncomingMessage to render
            payload: { message: "Do you wish to improve your idealized candidate further? I can suggest some improvements. Which part would you like to work around?" },
            result: {
                message: "Do you wish to improve your idealized candidate further? I can suggest some improvements. Which part would you like to work around?",
                from_ai: true,
                is_initial: true,
                title: {
                    must_have: 'Must-Haves',
                    should_have: 'Should-Haves',
                    good_to_have: 'Good-to-Haves',
                },
            },
            conversation_id: convId,
        };

        setChatList((prev: ChatMessage[]) => [...prev, initialMsg]);
    }, [conversationId]);

    // Trigger initial message when chat list is empty and not loading
    useEffect(() => {
        if (!isLoading && chatList.length === 0 && conversationId) {
            addInitialMessage();
        }
    }, [isLoading, chatList.length, conversationId, addInitialMessage]);

    const fetchUserDetails = useFetchUserDetails();

    // Memoize requestPayload to avoid recreation on every render
    // Include parent_id only if it exists in this conversation's history
    const requestPayload = useCallback((options: any) => {
        // Determine section-click vs free-text based on type of options
        const isSectionClick = options && typeof options === 'object' && !Array.isArray(options);
        const isFreeText = typeof options === 'string';

        const convId = conversationId;
        if (!convId) {
            return null;
        }

        const body: any = {
            payload: {
                title: isSectionClick ? options : null,
                message: isFreeText ? options : null,
            },
            is_multi_select: Array.isArray(options) && options?.length > 1,
            conversation_id: convId,
            payload_type: NUMBERS.THREE,
            user: userCompanyDetails?.userId,
        };
        // Only include parent_id if it's a valid backend ID (not a temporary local ID)
        const isValidParentId = !!lastMessageId &&
            typeof lastMessageId === 'number' &&
            Array.isArray(chatList) &&
            chatList.some((m: any) => m?.id === lastMessageId && !m?.is_local);
        if (isValidParentId) {
            body.parent_id = lastMessageId;
        }
        return body;
    }, [lastMessageId, conversationId, userCompanyDetails, chatList]);

    // Send message handler with optimistic update
    const handleSendMessage = async (options: any, initialAnswer: any) => {
        if (messageRef.current) {
            messageRef.current.value = '';
            messageRef.current.style.height = '56px';
        }

        // Note: ChatWithICP now behaves like ChatWithJob - always triggers initial message after suggestions

        const payload = requestPayload(options);
        if (!payload) {
            return;
        }

        // Deep copy the payload object to avoid shallow copy issues
        const newMessage = {
            id: `temp_${Date.now()}`,
            payload: { ...payload.payload },
            is_multi_select: payload.is_multi_select,
            parent_id: payload.parent_id,
            conversation_id: payload.conversation_id,
            payload_type: payload.payload_type,
            user: payload.user,
        };

        // No persistence of section target for ICP; routing is driven by message context

        // Optimistically update chat list with outgoing message
        setChatList((prevChatList: any) => [...prevChatList, newMessage]);
        setShowSendButton(false);
        try {
            setIsLoading(true);
            const response = await dispatch(jobAIChat({ payload, id: jobId, step })).unwrap();
            setInputMessage('');

            // Add the incoming message from the API response directly
            if (response?.data) {
                setChatList((prevChatList: any) => [...prevChatList, response.data]);
                // Update lastMessageId from the AI response for next message
                if (response?.data?.id) {
                    setLastMessageId(response.data.id);
                }

                // Add initial message after out-of-context or general info messages
                if (response?.data?.result?.out_of_context === true || response?.data?.result?.general_info === true) {
                    setTimeout(() => addInitialMessage(), 100);
                }



                // Don't reset flag here - we only reset when a new flow starts (new request)
            }
            setIsLoading(false);
        } catch (error) {
            console.info('Error sending message:', error);
            setIsLoading(false);
        }
    };

    const send = (event: any) => {
        event.preventDefault();

        const messageInput = messageRef.current;

        if (messageInput) {
            const trimmedMessage = messageInput.value.trim();

            if (trimmedMessage) {
                setShowSendButton(true);
                handleSendMessage(trimmedMessage, null);
                messageInput.value = '';
                messageInput.style.height = '56px';
            }
        }
    };

    const adjustTextareaSizeAndNotifyTyping = (event: any) => {
        const { value, scrollHeight } = event?.target;
        const isEmpty = value === '' || value === '\n';

        if (isEmpty) {
            event.target.style.height = '56px';
        } else {
            event.target.style.height = `${scrollHeight}px`;
        }
    };

    const handleEnterKeyPress = (event: any) => {
        if ((event.key === 'Enter') && !event.shiftKey) {
            event.preventDefault();
            send(event);
        } else if ((event.key === 'Enter') && event.shiftKey) {
            event.stopPropagation();
        }
    };

    // Scroll to bottom when chat list is updated or loader changes
    useEffect(() => {
        if (chatList.length) {
            scrollToBottom();
        }
    }, [chatList, isLoading]);

    useEffect(() => {
        const convId = conversationId;
        if (!hasLoaded && convId) {
            fetchChatDetails();
            fetchUserDetails().then(setUserCompanyDetails);
            setHasLoaded(true);
        }
    }, [fetchChatDetails, fetchUserDetails, conversationId, hasLoaded]);

    // Reset parent tracking when conversation changes or history is cleared
    useEffect(() => {
        setLastMessageId(null);
    }, [conversationId]);

    useEffect(() => {
        if (chatList.length === 0) {
            setLastMessageId(null);
        }
    }, [chatList.length]);

    // Note: No auto-generation polling on ICP per current requirements

    return (
        <>
            <div className="container">
                <div className="client-chat-main">
                    <div className="messaging">
                        <div className="inbox_msg">
                            <div className="chat-ai-profile-wrap">
                                {!hideChatWindow && (
                                    <OverlayTrigger
                                        placement="top"
                                        overlay={
                                            <Tooltip>
                                                Back
                                            </Tooltip>
                                        }
                                    >
                                        <div className="back-btn">
                                            <Image
                                                src={backBtn}
                                                height={25}
                                                width={25}
                                                alt="Back"
                                                className="back-btn-arrow"
                                                onClick={() => setHideChatWindow(true)}
                                            />
                                        </div>
                                    </OverlayTrigger>
                                )}
                                <h3 className="chat-profile-head">
                                    Hunter AI - Idealized Candidate
                                </h3>
                                <img src={climateLogo} alt="profile" className="chat-ai-profile-img" />
                            </div>
                            <div className="mesgs" ref={chatContainerRef}>
                                <div className="msg_history">

                                    {chatList?.length > 0 && chatList.map((chat: any, index: number) => (
                                        <div key={chat?.id || chat?.timestamp} className='incoming-outgoing-wrap'>
                                            {(chat?.payload_type !== NUMBERS.FOUR) && (
                                                <OutgoingMessage
                                                    chat={chat}
                                                    companyLogo={userCompanyDetails?.logo}
                                                    isFromJob={true}
                                                />
                                            )}
                                            {(chat?.payload_type === NUMBERS.FOUR) && (
                                                <IncomingMessage
                                                    chat={chat}
                                                    isLoading={isLoading}
                                                    setEnableGenerateResponse={() => { }}
                                                    message={chat?.result}
                                                    setLastMessageId={setLastMessageId}
                                                    handleSendMessage={handleSendMessage}
                                                    isFromJob={true}
                                                    componentType="icp"
                                                    onMoveSuggestions={async (sectionKey: string, suggestions: string[]) => {
                                                        const convId = conversationId;
                                                        if (!convId) {
                                                            return;
                                                        }
                                                        try {
                                                            setIsLoading(true);
                                                            const sectionLabels: Record<string, string> = {
                                                                'must_have': 'Must-Haves',
                                                                'should_have': 'Should-Haves',
                                                                'good_to_have': 'Good-to-Haves',
                                                                'could_have': 'Could-Haves',
                                                            };
                                                            for (const s of suggestions) {
                                                                // eslint-disable-next-line no-await-in-loop
                                                                await dispatch(
                                                                    moveICPSuggestion({
                                                                        conversation_id: convId,
                                                                        suggestion: s,
                                                                        target_section: (sectionKey || 'must_have') as any,
                                                                    }),
                                                                ).unwrap();
                                                            }
                                                            const sectionLabel = sectionLabels[sectionKey] || sectionKey;
                                                            const selectedCount = suggestions.length;
                                                            const summaryMsg = {
                                                                id: `temp_${Date.now()}`,
                                                                payload_type: 4,
                                                                payload: { message: `Added ${selectedCount} suggestion(s) to ${sectionLabel}.` },
                                                                result: {
                                                                    message: `Added ${selectedCount} suggestion(s) to ${sectionLabel}.`,
                                                                    from_ai: true,
                                                                },
                                                                conversation_id: convId,
                                                                is_local: true,
                                                            };
                                                            setChatList((prev: any) => [...prev, summaryMsg]);
                                                            if (typeof refreshJobData === 'function') {
                                                                refreshJobData(false);
                                                            }
                                                            setTimeout(() => addInitialMessage(), 100);

                                                        } catch (error: any) {
                                                            // Generic error message for actual failures
                                                            const errorMsgObj = {
                                                                id: `temp_${Date.now()}`, // Use temporary ID that won't be used as parent_id
                                                                payload_type: 4, // NUMBERS.FOUR - required for IncomingMessage to render
                                                                payload: { message: `Failed to move suggestion. Please try again.` },
                                                                result: {
                                                                    message: `Failed to move suggestion. Please try again.`,
                                                                    from_ai: true,
                                                                },
                                                                conversation_id: convId,
                                                                is_local: true, // Mark as local message
                                                            };
                                                            setChatList((prev: any) => [...prev, errorMsgObj]);
                                                        } finally {
                                                            setIsLoading(false);
                                                        }
                                                    }}
                                                    isLastMessage={index === chatList.length - 1}
                                                />
                                            )}
                                        </div>
                                    ))}

                                    {isLoading && (
                                        <div className="wrapper bounce-loader-wrapper">
                                            <div className="circle"></div>
                                            <div className="circle"></div>
                                            <div className="circle"></div>
                                        </div>
                                    )}
                                </div>

                                {/* Quick actions for ICP when there is no history yet */}
                                {!isLoading && chatList.length === 0 && conversationId && (
                                    <div className={`chat-single-option no-image-option`}>
                                        <div
                                            className={`chat-option`}
                                            onClick={() => handleSendMessage({ must_have: 'Must-Haves' }, null)}
                                        >
                                            <p className="chat-option-des">Must-Haves</p>
                                        </div>
                                        <div
                                            className={`chat-option`}
                                            onClick={() => handleSendMessage({ should_have: 'Should-Haves' }, null)}
                                        >
                                            <p className="chat-option-des">Should-Haves</p>
                                        </div>
                                        <div
                                            className={`chat-option`}
                                            onClick={() => handleSendMessage({ good_to_have: 'Good-to-Haves' }, null)}
                                        >
                                            <p className="chat-option-des">Good-to-Haves</p>
                                        </div>
                                        <div
                                            className={`chat-option`}
                                            onClick={() => handleSendMessage({ could_have: 'Could-Haves' }, null)}
                                        >
                                            <p className="chat-option-des">Could-Haves</p>
                                        </div>
                                    </div>
                                )}

                                <div className="textarea-chat-row">
                                    <div className="textarea-wrap">
                                        <textarea
                                            className="form-control"
                                            id="exampleFormControlTextarea1"
                                            rows={3}
                                            onInput={adjustTextareaSizeAndNotifyTyping}
                                            placeholder="Type here to ask anything"
                                            onChange={handleInputChange}
                                            ref={messageRef}
                                            onKeyDown={(e) => handleEnterKeyPress(e)}
                                        />
                                        <img
                                            src={chatPlaneIcon}
                                            alt="send"
                                            className={`chat-send-btn ${showSendButton ? '' : 'd-none'}`}
                                            onClick={() => handleSendMessage(inputMessage, null)}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default ChatWithICP;

